import React, { useState, useEffect } from 'react';
import { LoadingAnimatedInline } from '../components/Loading';
import { handleGetDownload } from '../downloads';
import {
  type AdminConfirmationPopupProps,
  AdminConfirmationPopup
} from '../components/AdminConfirmationPopup';

import { formatSize, formatDate } from './Util';
import { adminDownloadsGet, type AdminGetDownloads_Data } from './Downloads';
 
import { useAuth } from '../auth/AuthContext';

function getFileIcon(webIconPath?: string): string {
  if (!webIconPath) {
    return '';
  }
  return '/icons/' + webIconPath;
}

function getHistoryFileName(storagePath?: string): string {
  if (!storagePath) {
    return "download";
  }
  const parts = storagePath.split("/");
  return parts[parts.length - 1] || storagePath;
}

function parseUploadedAt(value?: Date | number | string): string {
  if (!value) {
    return "";
  }

  if (typeof value === "string") {
    return value;
  }

  if (typeof value === "number") {
    return new Date(value).toLocaleString();
  }

  if (value instanceof Date) {
    return value.toLocaleString();
  }

  return "";
}

const DownloadsList: React.FC = () => {
  const { user } = useAuth();

  const [downloads, setDownloads] = useState<AdminGetDownloads_Data[]>([]);
  const [loading, setLoading] = useState(true);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showHistoryDeleteConfirmation, setShowHistoryDeleteConfirmation] = useState(false);
  const [expandedHistory, setExpandedHistory] = useState<Set<string>>(new Set());

  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingPath, setPendingPath] = useState<string | null>(null);
  const [pendingDeleteKey, setPendingDeleteKey] = useState<string | null>(null);
  const [pendingHistoryDelete, setPendingHistoryDelete] = useState<{ key: string; storagePath: string } | null>(null);

  useEffect(() => {
    const fetchDownloads = async () => {
      let data: AdminGetDownloads_Data[] = [];
      try {
        data = await adminDownloadsGet();
      } catch (e) {
        console.error('Error fetching downloads:', e);
        alert('Failed to fetch downloads');
      }
      setDownloads(data);
      setLoading(false);
    };
    fetchDownloads();
  }, []);

  // upload to server; called after user confirms
  const handleReplace = async (path: string, file: File) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('storagePath', path);

      if (!user) throw new Error('Not authenticated');
      const token = await user.getIdToken();

      const res = await fetch('/api/admin/replaceDownload', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });

      if (!res.ok) {
        throw new Error('Upload failed');
      }

      const data = await res.json();
      setDownloads((prev) =>
        prev.map((item) =>
          item.storagePath === path ? { ...item, ...data } : item
        )
      );

      alert('Download Successfully Replaced');
    } catch (e) {
      console.error('Error replacing download:', e);
      alert('Failed to replace download');
    }
  };

  const handleFileSelection = (path: string, file: File) => {
    setPendingFile(file);
    setPendingPath(path);
    setShowConfirmation(true);
  };

  const handleConfirmReplace = () => {
    if (pendingFile && pendingPath) {
      handleReplace(pendingPath, pendingFile);
    }
    setShowConfirmation(false);
    setPendingFile(null);
    setPendingPath(null);
  };

  const handleCancelReplace = () => {
    setShowConfirmation(false);
    setPendingFile(null);
    setPendingPath(null);
  };

  // delete from server; called after user confirms
  const handleDelete = async (key: string) => {
    try {
      if (!user) throw new Error('Not authenticated');
      const token = await user.getIdToken();

      const res = await fetch('/api/admin/deleteDownload', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ key })
      });

      if (!res.ok) {
        throw new Error('Delete failed');
      }

      setDownloads((prev) =>
        prev.filter((item) => item.key !== key)
      );

      alert('Download Successfully Deleted');
    } catch (e) {
      console.error('Error deleting download:', e);
      alert('Failed to delete download');
    }
  };

  const handleConfirmDelete = () => {
    if (pendingDeleteKey) {
      handleDelete(pendingDeleteKey);
    }
    setShowDeleteConfirmation(false);
    setPendingDeleteKey(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
    setPendingDeleteKey(null);
  };

  // delete history item from server; called after user confirms
  const handleDeleteHistoryItem = async (key: string, storagePath: string) => {
    try {
      if (!user) throw new Error('Not authenticated');
      const token = await user.getIdToken();

      const res = await fetch('/api/admin/deleteHistoryItem', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ key, storagePath })
      });

      if (!res.ok) {
        throw new Error('Delete failed');
      }

      setDownloads((prev) =>
        prev.map((item) =>
          item.key === key
            ? {
                ...item,
                history: (item.history || []).filter(
                  (h) => h.storagePath !== storagePath
                ),
              }
            : item
        )
      );

      alert('History item successfully deleted');
    } catch (e) {
      console.error('Error deleting history item:', e);
      alert('Failed to delete history item');
    }
  };

  const handleConfirmHistoryDelete = () => {
    if (pendingHistoryDelete) {
      handleDeleteHistoryItem(
        pendingHistoryDelete.key,
        pendingHistoryDelete.storagePath
      );
    }
    setShowHistoryDeleteConfirmation(false);
    setPendingHistoryDelete(null);
  };

  const handleCancelHistoryDelete = () => {
    setShowHistoryDeleteConfirmation(false);
    setPendingHistoryDelete(null);
  };

  const toggleHistoryExpanded = (storagePath: string) => {
    setExpandedHistory((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(storagePath)) {
        newSet.delete(storagePath);
      } else {
        newSet.add(storagePath);
      }
      return newSet;
    });
  };

  const popupProps: AdminConfirmationPopupProps = {
    innerHTML: (
      <div className="w-full h-full flex flex-col items-center justify-center gap-4">
        <p className="w-full text-lg text-center">
          Are you sure you want to replace this download? This action cannot be
          reversed.
        </p>
        {pendingFile && (
          <div className="w-full mt-4 text-sm text-gray-700">
            <p>
              <strong>Selected file:</strong> {pendingFile.name}
            </p>
            <p>
              <strong>Size:</strong> {formatSize(pendingFile.size)}
            </p>
            <p>
              <strong>Type:</strong> {pendingFile.type || 'Unknown'}
            </p>
          </div>
        )}
      </div>
    ),
    onConfirm: handleConfirmReplace,
    onCancel: handleCancelReplace
  };

  const deletePopupProps: AdminConfirmationPopupProps = {
    innerHTML: (
      <div className="w-full h-full flex flex-col items-center justify-center gap-4">
        <p className="w-full text-lg text-center">
          Are you sure you want to delete this download? This action cannot be
          reversed.
        </p>
        {pendingDeleteKey && (
          <div className="w-full mt-4 text-sm text-gray-700">
            <p>
              <strong>Document Key:</strong> {pendingDeleteKey}
            </p>
          </div>
        )}
      </div>
    ),
    onConfirm: handleConfirmDelete,
    onCancel: handleCancelDelete
  };

  const historyDeletePopupProps: AdminConfirmationPopupProps = {
    innerHTML: (
      <div className="w-full h-full flex flex-col items-center justify-center gap-4">
        <p className="w-full text-lg text-center">
          Are you sure you want to delete this history item? This action cannot
          be reversed.
        </p>
        {pendingHistoryDelete && (
          <div className="w-full mt-4 text-sm text-gray-700">
            <p>
              <strong>Storage Path:</strong> {pendingHistoryDelete.storagePath}
            </p>
          </div>
        )}
      </div>
    ),
    onConfirm: handleConfirmHistoryDelete,
    onCancel: handleCancelHistoryDelete
  };

  if (loading) return <LoadingAnimatedInline />;
  return (
    <div className="p-6 max-w-5/6 mx-auto h-full">
      <h2 className="text-2xl font-bold mb-6">Downloads Management</h2>
      <div className="grid gap-4">
        {downloads.map((item, index) => (
          <div
            key={item.storagePath}
            className="bg-white border border-light-border rounded-lg shadow-sm p-5"
          >
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-4">
                {item.webIcon ? (
                  <img
                    src={getFileIcon(item.webIcon)} 
                    alt={`${item.displayName} icon`}
                    className="w-8 h-8 object-contain"
                  />
                ) : (
                  <div className="w-8 h-8 bg-gray-200 flex items-center justify-center rounded">
                    <span className="text-gray-500 text-sm">No Icon</span>
                  </div>
                )}
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-light-text">
                  {item.displayName}
                </h3>
                <div className="mt-2 grid grid-cols-1 md:grid-cols-4 gap-2 text-sm text-gray-600">
                  <div>
                    <span className="font-medium text-gray-700">Version:</span>
                    <p className="text-gray-600">{item.currentVersion}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Size:</span>
                    <p className="text-gray-600">{formatSize(item.size)}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">
                      Last Updated:
                    </span>
                    <p className="text-gray-600">
                      {formatDate(item.uploadedAt)}
                    </p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">
                      Storage Path:
                    </span>
                    <p className="text-gray-600">{item.storagePath}</p>
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <input
                  type="file"
                  id={`file-${index}`}
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      handleFileSelection(item.storagePath, file);
                    }
                    e.target.value = '';
                  }}
                />
                <button
                  onClick={() =>
                    document.getElementById(`file-${index}`)?.click()
                  }
                  className="px-4 py-2 bg-light-main text-white rounded-md hover:bg-light-main-hover transition-colors duration-200 ease-in-out font-medium cursor-pointer"
                >
                  Replace File
                </button>
                <button
                  onClick={() => handleGetDownload(item.storagePath)}
                  className="px-4 py-2 bg-light-bg border border-light-border text-light-text rounded-md hover:bg-gray-100 transition-colors duration-200 ease-in-out font-medium cursor-pointer"
                >
                  Download
                </button>
                <button
                  onClick={() => {
                    setPendingDeleteKey(item.key);
                    setShowDeleteConfirmation(true);
                  }}
                  className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors duration-200 ease-in-out font-medium cursor-pointer"
                >
                  Delete
                </button>
              </div>
            </div>

            {/* History Accordion */}
            <div className="mt-4 pt-4 border-t border-light-border">
              <div className="flex justify-start">
                <button
                  className="px-2 py-1 text-sm text-light-text-alt bg-light-main border border-light-border rounded hover:bg-light-main-hover duration-200 ease-in-out cursor-pointer"
                  onClick={() => toggleHistoryExpanded(item.storagePath)}
                  aria-label={expandedHistory.has(item.storagePath) ? 'Collapse history' : 'Expand history'}
                >
                  {expandedHistory.has(item.storagePath) ? 'Hide History' : 'Show History'}
                </button>
              </div>

              {expandedHistory.has(item.storagePath) && (
                <div className="border-t border-light-border pt-3 mt-3">
                  <div className="space-y-3">
                    {item.history && item.history.length > 0 ? (
                      item.history
                        .slice()
                        .reverse()
                        .map((historyItem, histIndex) => {
                          const uploadedAt = parseUploadedAt(historyItem.uploadedAt);
                          const fileName = getHistoryFileName(historyItem.storagePath);

                          return (
                            <div
                              key={historyItem.storagePath || histIndex}
                              className="flex flex-col gap-2 p-3 rounded border border-light-border bg-slate-50"
                            >
                              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                <div className="flex-1">
                                  <div className="text-sm font-medium text-light-text">
                                    {uploadedAt || 'Unknown upload date'}
                                  </div>
                                  <div className="text-xs text-gray-600 mt-1">
                                    {historyItem.storagePath}
                                  </div>
                                  <div className="text-xs text-gray-600">
                                    Size: {formatSize(historyItem.size || 0)}
                                  </div>
                                </div>
                                <div className="flex gap-2">
                                  {historyItem.downloadURL ? (
                                    <a
                                      href={historyItem.downloadURL}
                                      target="_blank"
                                      rel="noreferrer noopener"
                                      download={fileName}
                                    >
                                      <button
                                        type="button"
                                        className="px-3 py-1 text-sm text-light-text-alt bg-light-main border border-light-border rounded hover:bg-light-main-hover duration-200 ease-in-out"
                                      >
                                        Download
                                      </button>
                                    </a>
                                  ) : (
                                    <button
                                      type="button"
                                      disabled
                                      className="px-3 py-1 text-sm text-light-text-alt bg-gray-200 border border-light-border rounded cursor-not-allowed"
                                    >
                                      No link
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setPendingHistoryDelete({
                                        key: item.key,
                                        storagePath: historyItem.storagePath || ''
                                      });
                                      setShowHistoryDeleteConfirmation(true);
                                    }}
                                    className="px-2 py-1 text-sm text-white bg-red-500 border border-red-600 rounded hover:bg-red-600 duration-200 ease-in-out font-medium cursor-pointer"
                                    title="Delete this history item"
                                  >
                                    ✕
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })
                    ) : (
                      <div className="text-sm text-light-text">No history available.</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      {showConfirmation && <AdminConfirmationPopup {...popupProps} />}
      {showDeleteConfirmation && <AdminConfirmationPopup {...deletePopupProps} />}
      {showHistoryDeleteConfirmation && <AdminConfirmationPopup {...historyDeletePopupProps} />}
    </div>
  );
};

export default DownloadsList;
